import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {riskOverrideUrl} from "../services/categoryProfileUrls";
import type {RiskFactor, RiskScore} from "../types/categoryProfileTypes";
import {done, failed} from "./notices";

export type RiskOverrideInput = {level: 'high' | 'medium' | 'low' | null; excluded: RiskFactor['key'][]; reason: string};

/** The team's input on a category's risk: set, remove, keep after a refresh. Every change reads the profile and Explore
 * again (the level shows in both). */
export function useRiskActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const refresh = () => qc.invalidateQueries({predicate: q => String(q.queryKey[0]).startsWith(`category_profile/${databagId}/`)});
        const send = async (method: 'put' | 'delete' | 'post', url: string, data?: unknown) => {
            const res = await queryHttp.request<{risk: RiskScore | null}>({method, url, data});
            void refresh();
            return res.data.risk;
        };
        return {
            /** Rejects so the dialog can show the error. */
            set: async (input: RiskOverrideInput) => {
                await send('put', riskOverrideUrl(databagId, path), input);
                done('Risk set by the team.');
            },
            remove: async () => {
                try {
                    await send('delete', riskOverrideUrl(databagId, path));
                    done('Back to the calculated risk.');
                } catch (e) {
                    failed(e);
                }
            },
            keep: async () => {
                try {
                    await send('post', riskOverrideUrl(databagId, path, 'acknowledge'));
                    done("The team's risk is kept.");
                } catch (e) {
                    failed(e);
                }
            },
        };
    }, [qc, databagId, path]);
}
